import type { ImageArea } from "@/lib/types";
import { getCropAreaPixelDimensions } from "@/lib/print";

export type CropSettings = {
  x: number;
  y: number;
  zoom: number;
};

export type ImageCrop = CropSettings & {
  sourceWidth: number;
  sourceHeight: number;
};

export type CropWindow = {
  left: number;
  top: number;
  width: number;
  height: number;
};

export type CropCorner = "top-left" | "top-right" | "bottom-left" | "bottom-right";

export function clamp(value: number, min: number, max: number) {
  return Math.min(max, Math.max(min, value));
}

export function getCropGeometry(
  area: ImageArea,
  crop: CropSettings,
  sourceWidth: number,
  sourceHeight: number,
) {
  const { width, height } = getCropAreaPixelDimensions(area);
  const scale =
    Math.max(width / sourceWidth, height / sourceHeight) * crop.zoom;
  const drawWidth = sourceWidth * scale;
  const drawHeight = sourceHeight * scale;
  const windowWidth = width / drawWidth;
  const windowHeight = height / drawHeight;
  const windowLeft =
    0.5 + (crop.x * (1 - windowWidth)) / 2 - windowWidth / 2;
  const windowTop =
    0.5 + (crop.y * (1 - windowHeight)) / 2 - windowHeight / 2;

  return {
    width,
    height,
    drawX: -windowLeft * sourceWidth * scale,
    drawY: -windowTop * sourceHeight * scale,
    drawWidth,
    drawHeight,
    window: {
      left: windowLeft,
      top: windowTop,
      width: windowWidth,
      height: windowHeight,
    },
  };
}

export function getCropStateForWindow(
  area: ImageArea,
  crop: ImageCrop,
  window: CropWindow,
): CropSettings {
  const initialGeometry = getCropGeometry(
    area,
    { x: 0, y: 0, zoom: 1 },
    crop.sourceWidth,
    crop.sourceHeight,
  );
  const zoom = clamp(initialGeometry.window.width / window.width, 1, 3);
  const centerX = window.left + window.width / 2;
  const centerY = window.top + window.height / 2;
  const horizontalTravel = (1 - window.width) / 2;
  const verticalTravel = (1 - window.height) / 2;
  return {
    zoom,
    x:
      horizontalTravel > 0
        ? clamp((centerX - 0.5) / horizontalTravel, -1, 1)
        : 0,
    y:
      verticalTravel > 0
        ? clamp((centerY - 0.5) / verticalTravel, -1, 1)
        : 0,
  };
}

export function resizeCropWindow(
  window: CropWindow,
  corner: CropCorner,
  deltaX: number,
  deltaY: number,
  stageWidth: number,
  stageHeight: number,
  aspectRatio: number,
): CropWindow {
  const leftSide = corner.endsWith("left");
  const topSide = corner.startsWith("top");
  const left = window.left * stageWidth;
  const top = window.top * stageHeight;
  const width = window.width * stageWidth;
  const height = window.height * stageHeight;
  const anchorX = leftSide ? left + width : left;
  const anchorY = topSide ? top + height : top;
  const maxWidth = Math.min(
    leftSide ? anchorX : stageWidth - anchorX,
    (topSide ? anchorY : stageHeight - anchorY) * aspectRatio,
  );
  const minWidth = Math.min(maxWidth, 36);
  const nextWidth = clamp(
    Math.max(
      width + (leftSide ? -deltaX : deltaX),
      (height + (topSide ? -deltaY : deltaY)) * aspectRatio,
    ),
    minWidth,
    maxWidth,
  );
  const nextHeight = nextWidth / aspectRatio;
  const nextLeft = leftSide ? anchorX - nextWidth : anchorX;
  const nextTop = topSide ? anchorY - nextHeight : anchorY;

  return {
    left: nextLeft / stageWidth,
    top: nextTop / stageHeight,
    width: nextWidth / stageWidth,
    height: nextHeight / stageHeight,
  };
}
